// The game's screens as a small state machine (2026-10-25, ui branch). Pure, no DOM: main.ts and screens.ts ask it
// where a press leads, and tests/ui.test.ts walks every path. A move a screen doesn't list is ignored, so a late tap
// (a second Play, a pause during the army pick) can't put the game somewhere it shouldn't be.

export type Screen = 'title' | 'lobby' | 'hero' | 'pick' | 'play' | 'pause' | 'end';
export type Action = 'play' | 'watch' | 'online' | 'begin' | 'hero' | 'heroPicked' | 'pick' | 'picked' | 'cancel' | 'pause' | 'resume' | 'end' | 'again' | 'title';

const MOVES: Record<Screen, Partial<Record<Action, Screen>>> = {
  title: { play: 'play', watch: 'play', online: 'lobby' },
  // online (2026-11-01): the lobby finds or makes a room; its match begins when the room says so (begin)
  lobby: { begin: 'play', title: 'title' },
  play: { hero: 'hero', pick: 'pick', pause: 'pause', end: 'end' },
  // the hero pick opens at the start of a match you play; there is no Not yet, a match needs your hero
  hero: { heroPicked: 'play' },
  // online the match runs on under these cards, so a fall or a win can end it there (Flash F2)
  pick: { picked: 'play', cancel: 'play', end: 'end' },
  pause: { resume: 'play', title: 'title', end: 'end' },
  // after a loss the match keeps going behind the card: watch it, start again, or leave
  end: { watch: 'play', again: 'play', title: 'title', end: 'end', online: 'lobby' },
};

/** Where `a` leads from `s`; `s` itself when the move isn't allowed there. */
export function next(s: Screen, a: Action): Screen { return MOVES[s][a] ?? s; }

/** The sim's clock stops while you choose your hero or army, or the game is paused. The title (and the lobby) run the attract
 *  match behind them, and the end card lets the match play on after you fall. An online match never stops: main.ts steps
 *  it by the room's turns whatever screen is open. */
export function clockRuns(s: Screen) { return s !== 'hero' && s !== 'pick' && s !== 'pause'; }

/** Any of these in the address skips the title straight into a match, so scripts/capture.ts, scripts/fps.ts and old
 *  links keep working unchanged. ?title=1 shows the title anyway (QA shots of it with ?qa=1). */
export const SKIP_PARAMS = ['seed', 'bots', 'army', 'autoplay', 'qa', 'capture', 'armies', 'premium', 'zoom', 'live', 'jump'] as const;
export function startScreen(q: URLSearchParams): 'title' | 'play' {
  if (q.get('title') === '1') return 'title';
  return SKIP_PARAMS.some((k) => q.has(k)) ? 'play' : 'title';
}
